import { Result } from "@/api/types";
import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import { dateFormat, useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import { filterSearchToGetFilter, sortToParam } from "@/utilities/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { IImpact, ImpactMetricKey } from "../utils/types";

interface GetCustomerImpactTotals extends Result {
  result: {
    customer_impact: IImpact;
    company_impact: IImpact;
  };
}

export function useCustomerImpactTotals({ metrics }: { metrics: ImpactMetricKey[] }) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: ["dashboard", "customer", "totals", startDate, endDate, metrics],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetCustomerImpactTotals>("/dashboard/customer/totals", {
          params: {
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            metrics: metrics.join(";"),
          },
        })
        .then((result) => result.data.result),
  });

  return query;
}

interface GetCustomerImpactPerEol extends Result {
  result: {
    total_rows: number;
    customer_impact: ({
      impact_type: string;
      name: string;
      unit: string;
      quantity: number;
    } & IImpact)[];
    company_customer_impact: IImpact;
  };
}

export function useCustomerImpactPerType({
  pagination,
  filter,
  search,
  sort,
  metric,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: [
      "dashboard",
      "customer",
      "per_eol",
      startDate,
      endDate,
      pagination,
      filter,
      search,
      sort,
      metric,
    ],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetCustomerImpactPerEol>("/dashboard/customer/per_type", {
          params: {
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            metric,
          },
        })
        .then((result) => result.data.result),
  });

  return query;
}

export function useCustomerImpactPerTypeExport({
  pagination,
  filter,
  search,
  sort,
  metric,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();

  return useMutation({
    mutationFn: () =>
      axios
        .get<Result<{ url: string }>>("/dashboard/customer/per_type", {
          params: {
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            export: true,
            metric,
          },
        })
        .then((result) => result.data.result.url),
  });
}
